import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { StringProgress } from "./StringProgress";

/**
 * The `StringParallax` class extends the `StringProgress` class to provide
 * functionality for handling parallax effects on scrollable elements.
 * It maps specific attributes related to parallax and calculates the
 * necessary transformations based on scroll progress and viewport size.
 */
export class StringParallax extends StringProgress {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "parallax";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "parallax", type: "number", fallback: this.settings["parallax"] },
      {
        key: "parallax-bias",
        type: "number",
        fallback: this.settings["parallax-bias"],
      },
    ];
  }

  /**
   * Called when an object is initialized.
   */
  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    const bias = object.getProperty<number>("parallax-bias") ?? 0.0;
    const factor = Math.abs(object.getProperty<number>("parallax") ?? 0.2);
    object.setProperty("parallax-sign", Math.sign(object.getProperty<number>("parallax")));
    object.setProperty<number>("parallax", factor);
    object.setProperty("parallax-position-start", -0.5 + 0.5 * bias);
    object.setProperty("parallax-position-end", 0.5 + 0.5 * (1 - bias));

    const screenSize = this.data.viewport.windowHeight;

    object.setProperty("offset-top", factor * screenSize);
    object.setProperty("offset-bottom", factor * screenSize);
  }

  /**
   * Called on scroll.
   */
  override onScroll(data: StringData): void {
    super.onScroll(data);
    this.scrollHandler();
  }

  /**
   * Called on resize.
   */
  override onResize(): void {
    const isDesktop = window.innerWidth > 1080;
    this.scrollHandler = isDesktop ? this.handleScrollDesktop : this.handleScrollMobile;

    this.objects.forEach((object) => {
      const transform = `translate3d(0, 0px, 0)`;
      this.applyToElementAndConnects(object, (el) => {
        el.style.transform = transform;
      });
    });
    // if (isDesktop) {
    //   this.handleScrollDesktop();
    // }
  }

  private handleScrollDesktop = (): void => {
    this.objects.forEach((object) => {
      const progress = object.getProperty<number>("progress") ?? 0;
      const factor = object.getProperty<number>("parallax") ?? 0;
      const start = object.getProperty<number>("parallax-position-start") ?? 0;
      const end = object.getProperty<number>("parallax-position-end") ?? 1;
      const sign = object.getProperty<number>("parallax-sign") ?? 1;

      const screenSize = this.data.viewport.windowHeight;
      const translation = sign * factor * (screenSize * start + progress * screenSize * end);

      this.events.emit(`object:parallax:${object.id}`, translation);
      const transform = `translate3d(0, ${translation}px, 0)`;
      this.applyToElementAndConnects(object, (el) => {
        el.style.transform = transform;
      });
    });
  };

  private handleScrollMobile = (): void => {};

  private scrollHandler = this.handleScrollDesktop;
}
